import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './Entrepreneuriat.css';
import { 
  FaLightbulb,
  FaRocket,
  FaChartLine,
  FaFileInvoice,
  FaHandshake,
  FaUsers,
  FaLaptopCode,
  FaArrowLeft,
  FaCheckCircle,
  FaEuroSign,
  FaCalendarAlt,
  FaRegClock,
  FaBuilding,
  FaGraduationCap,
  FaGlobe,
  FaLinkedin,
  FaTwitter,
  FaInstagram,
  FaBook,
  FaTrophy,
  FaSeedling,
  FaChartBar,
  FaBullhorn,
  FaUserTie
} from 'react-icons/fa';

const Entrepreneuriat = () => {
  const navigate = useNavigate();

  const businessStructures = [
    {
      name: "Auto-entrepreneur (Micro-entreprise)",
      icon: <FaUserTie />,
      description: "Idéal pour démarrer seul, formalités simplifiées",
      advantages: [
        "Fiscalité simplifiée",
        "Cotisations sociales réduites",
        "Comptabilité allégée",
        "Plafond de CA annuel"
      ],
      limitations: [
        "Plafond de CA 77 700€ (prestations) / 188 700€ (ventes)",
        "Pas de déduction de charges",
        "Responsabilité personnelle"
      ],
      color: "#4ecdc4"
    },
    {
      name: "SASU (Société par Actions Simplifiée Unipersonnelle)",
      icon: <FaBuilding />,
      description: "Structure plus robuste pour un projet ambitieux",
      advantages: [
        "Responsabilité limitée",
        "Statut d'assimilé salarié",
        "Flexibilité dans l'organisation",
        "Déduction des charges"
      ],
      limitations: [
        "Formalités plus complexes",
        "Coûts de création plus élevés",
        "Obligations comptables"
      ],
      color: "#ff6b6b"
    },
    {
      name: "EURL (Entreprise Unipersonnelle à Responsabilité Limitée)",
      icon: <FaChartLine />,
      description: "Structure avec responsabilité limitée",
      advantages: [
        "Responsabilité limitée aux apports",
        "Régime social avantageux",
        "Capital minimal libre",
        "Déduction des charges"
      ],
      limitations: [
        "Formalités de création",
        "Comptabilité formelle",
        "Frais de gestion"
      ],
      color: "#ffd93d"
    }
  ];

  const supportPrograms = [
    {
      name: "PEPITE",
      organization: "Ministère de l'Enseignement Supérieur",
      description: "Statut National Étudiant-Entrepreneur (SNEE)",
      benefits: [
        "Accompagnement personnalisé",
        "Formation à l'entrepreneuriat",
        "Mise en réseau",
        "Diplôme D2E"
      ],
      website: "https://www.pepite-france.fr",
      color: "#1e3c72"
    },
    {
      name: "Bourse French Tech",
      organization: "BPI France",
      description: "Financement pour projets innovants",
      benefits: [
        "Jusqu'à 30 000€",
        "Accompagnement personnalisé",
        "Accès au réseau French Tech",
        "Visibilité médiatique"
      ],
      website: "https://www.bpifrance.fr",
      color: "#0055a5"
    },
    {
      name: "1 Jeune, 1 Solution",
      organization: "Gouvernement",
      description: "Aides pour les jeunes entrepreneurs",
      benefits: [
        "Aide à la création (jusqu'à 10 000€)",
        "Exonérations fiscales",
        "Accompagnement personnalisé",
        "Prêts garantis"
      ],
      website: "https://www.1jeune1solution.gouv.fr",
      color: "#e74c3c"
    },
    {
      name: "Incubateurs Universitaires",
      organization: "Universités et Grandes Écoles",
      description: "Accompagnement dans les établissements",
      benefits: [
        "Espace de coworking gratuit",
        "Mentorat par des experts",
        "Formations spécifiques",
        "Mise en relation investisseurs"
      ],
      website: "#",
      color: "#6c5ce7"
    }
  ];

  const startupStories = [
    {
      name: "Lydia",
      founders: "Cyril Chiche & Antoine Porte",
      description: "Application de paiement entre amis",
      success: "De 0 à 5 millions d'utilisateurs en 5 ans",
      lesson: "Partir d'un besoin simple et bien l'exécuter",
      year: "2013"
    },
    {
      name: "Deezer",
      founders: "Daniel Marhely & Jonathan Benassaya",
      description: "Plateforme de streaming musical",
      success: "Leader français du streaming, présent dans 180 pays",
      lesson: "S'adapter rapidement aux évolutions technologiques",
      year: "2007"
    },
    {
      name: "Blablacar",
      founders: "Frédéric Mazzella",
      description: "Plateforme de covoiturage",
      success: "Plus de 90 millions d'utilisateurs dans le monde",
      lesson: "Résoudre un problème concret de mobilité",
      year: "2006"
    },
    {
      name: "Doctolib",
      founders: "Stanislas Niox-Chateau",
      description: "Plateforme de prise de rendez-vous médicaux",
      success: "Leader européen, valorisée à 6,5 milliards d'€",
      lesson: "Simplifier le quotidien des professionnels et patients",
      year: "2013"
    }
  ];

  const keySteps = [
    {
      step: 1,
      title: "Idéation et validation",
      description: "Identifiez un problème à résoudre, validez votre idée auprès de futurs clients",
      icon: <FaLightbulb />,
      tasks: ["Brainstorming", "Étude de marché", "Entretiens clients", "Analyse concurrentielle"]
    },
    {
      step: 2,
      title: "Business Model",
      description: "Définissez comment vous allez générer des revenus",
      icon: <FaChartLine />,
      tasks: ["Modèle économique", "Plan de financement", "Prévisions financières", "Stratégie de pricing"]
    },
    {
      step: 3,
      title: "Prototypage",
      description: "Créez un produit minimum viable (MVP)",
      icon: <FaLaptopCode />,
      tasks: ["Wireframes", "Prototype fonctionnel", "Tests utilisateurs", "Itérations"]
    },
    {
      step: 4,
      title: "Statut juridique",
      description: "Choisissez la structure adaptée à votre projet",
      icon: <FaFileInvoice />,
      tasks: ["Choix du statut", "Rédaction des statuts", "Immatriculation", "Ouverture compte pro"]
    },
    {
      step: 5,
      title: "Recherche de financement",
      description: "Trouvez les fonds pour développer votre activité",
      icon: <FaEuroSign />,
      tasks: ["Prêts étudiants", "Bourses", "Love money", "Investisseurs"]
    },
    {
      step: 6,
      title: "Lancement",
      description: "Commercialisez votre produit et développez votre clientèle",
      icon: <FaRocket />,
      tasks: ["Marketing", "Ventes", "Communication", "Stratégie d'acquisition"]
    }
  ];

  const fundingSources = [
    {
      name: "Prêt Étudiant Entrepreneur",
      amount: "Jusqu'à 10 000€",
      conditions: "Projet validé par un établissement partenaire",
      timeline: "2-4 semaines",
      icon: <FaEuroSign />
    },
    {
      name: "Bourse French Tech",
      amount: "5 000€ - 30 000€",
      conditions: "Projet innovant, création récente",
      timeline: "3-6 mois",
      icon: <FaTrophy />
    },
    {
      name: "Love Money",
      amount: "Variable",
      conditions: "Fonds propres ou proches",
      timeline: "Flexible",
      icon: <FaHandshake />
    },
    {
      name: "Concours de Pitch",
      amount: "1 000€ - 50 000€",
      conditions: "Participation à des concours entrepreneuriat",
      timeline: "Selon concours",
      icon: <FaTrophy />
    },
    {
      name: "Incubateurs",
      amount: "Accompagnement + financement",
      conditions: "Sélection sur dossier",
      timeline: "6-12 mois",
      icon: <FaBuilding />
    },
    {
      name: "Crowdfunding",
      amount: "Variable",
      conditions: "Campagne de financement participatif",
      timeline: "1-3 mois",
      icon: <FaUsers />
    }
  ];

  const entrepreneurshipSkills = [
    "Résilience et persévérance",
    "Gestion du stress et incertitude",
    "Leadership et management",
    "Communication et storytelling",
    "Gestion financière",
    "Réseautage et vente",
    "Prise de décision rapide",
    "Adaptabilité et flexibilité",
    "Vision stratégique",
    "Créativité et innovation"
  ];

  const resources = [
    {
      type: "Podcasts",
      items: ["Génération Do It Yourself", "Startup Nation", "The Entrepreneur"]
    },
    {
      type: "Livres",
      items: ["L'Entrepreneuriat pour les Nuls", "Zero to One", "The Lean Startup"]
    },
    {
      type: "Sites web",
      items: ["BPI France", "Réseau Entreprendre", "CCI France"]
    },
    {
      type: "Événements",
      items: ["VivaTech", "Startup Weekend", "Forum des Entrepreneurs"]
    }
  ];

  const getColorByStep = (step) => {
    const colors = ["#4ecdc4", "#ff6b6b", "#ffd93d", "#6c5ce7", "#a8e6cf", "#ff8b94"];
    return colors[step - 1];
  };

  return (
    <div className="service-detail-page">
      {/* Hero Section */}
      <div className="service-hero entrepreneur-hero">
        <div className="service-hero-background"></div>
        <div className="service-hero-content">
          <button className="back-button" onClick={() => navigate('/association')}>
            <FaArrowLeft /> Retour aux services
          </button>
          <div className="service-hero-icon">
            <FaLightbulb />
          </div>
          <h1>Entrepreneuriat</h1>
          <p>Soutien pour les projets entrepreneuriaux des étudiants internationaux</p>
        </div>
      </div>

      <div className="service-content">
        <div className="service-container">
          {/* Introduction */}
          <div className="service-intro">
            <h2>De l'idée à l'entreprise</h2>
            <p>
              Vous avez une idée innovante et vous souhaitez créer votre entreprise pendant vos études ?
              L'entrepreneuriat étudiant est en plein essor en France. Découvrez tous les dispositifs d'accompagnement,
              les statuts juridiques adaptés, les sources de financement et les success stories inspirantes pour
              concrétiser votre projet.
            </p>
            <div className="intro-stats">
              <div className="stat">
                <span className="stat-number">1000+</span>
                <span className="stat-label">Startups étudiantes créées/an</span>
              </div>
              <div className="stat">
                <span className="stat-number">30k€</span>
                <span className="stat-label">Financement moyen disponible</span>
              </div>
              <div className="stat">
                <span className="stat-number">200+</span>
                <span className="stat-label">Incubateurs en France</span>
              </div>
            </div>
          </div>

          {/* Statuts juridiques */}
          <div className="structures-section">
            <h2>Statuts juridiques pour étudiants</h2>
            <div className="structures-grid">
              {businessStructures.map((structure, index) => (
                <div className="structure-card" key={index} style={{ borderTopColor: structure.color }}>
                  <div className="structure-icon" style={{ color: structure.color }}>
                    {structure.icon}
                  </div>
                  <h3>{structure.name}</h3>
                  <p className="structure-desc">{structure.description}</p>
                  <div className="structure-details">
                    <div className="advantages">
                      <h4>Avantages</h4>
                      <ul>
                        {structure.advantages.map((adv, i) => (
                          <li key={i}>
                            <FaCheckCircle className="check-icon" />
                            <span>{adv}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="limitations">
                      <h4>Limites</h4>
                      <ul>
                        {structure.limitations.map((lim, i) => (
                          <li key={i}>
                            <span className="limitation-icon">•</span>
                            <span>{lim}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Programmes d'accompagnement */}
          <div className="programs-section">
            <h2>Programmes d'accompagnement</h2>
            <div className="programs-grid">
              {supportPrograms.map((program, index) => (
                <div className="program-card" key={index}>
                  <div className="program-header" style={{ backgroundColor: program.color }}>
                    <h3>{program.name}</h3>
                    <span>{program.organization}</span>
                  </div>
                  <div className="program-content">
                    <p>{program.description}</p>
                    <div className="program-benefits">
                      {program.benefits.map((benefit, i) => (
                        <div className="benefit" key={i}>
                          <FaCheckCircle className="benefit-icon" />
                          <span>{benefit}</span>
                        </div>
                      ))}
                    </div>
                    <a 
                      href={program.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="program-link"
                      style={{ color: program.color }}
                    >
                      En savoir plus →
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Étapes clés */}
          <div className="steps-section">
            <h2>Les étapes de la création</h2>
            <div className="steps-timeline">
              {keySteps.map((step) => (
                <div className="step-item" key={step.step}>
                  <div className="step-marker" style={{ backgroundColor: getColorByStep(step.step) }}>
                    <div className="step-number">{step.step}</div>
                    <div className="step-icon">{step.icon}</div>
                  </div>
                  <div className="step-content">
                    <h3>{step.title}</h3>
                    <p>{step.description}</p>
                    <div className="step-tasks">
                      {step.tasks.map((task, i) => (
                        <span className="task-tag" key={i}>{task}</span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Sources de financement */}
          <div className="funding-section">
            <h2>Sources de financement</h2>
            <div className="funding-grid">
              {fundingSources.map((source, index) => (
                <div className="funding-card" key={index}>
                  <div className="funding-icon">{source.icon}</div>
                  <h3>{source.name}</h3>
                  <div className="funding-amount">{source.amount}</div>
                  <div className="funding-condition">{source.conditions}</div>
                  <div className="funding-timeline">
                    <FaRegClock className="clock-icon" />
                    <span>{source.timeline}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Success Stories */}
          <div className="stories-section">
            <h2>Success stories inspirantes</h2>
            <div className="stories-grid">
              {startupStories.map((story, index) => (
                <div className="story-card" key={index}>
                  <div className="story-year">{story.year}</div>
                  <h3>{story.name}</h3>
                  <p className="story-desc">{story.description}</p>
                  <div className="story-founders">
                    <FaUsers className="founder-icon" />
                    <span>{story.founders}</span>
                  </div>
                  <div className="story-success">
                    <FaTrophy className="success-icon" />
                    <span>{story.success}</span>
                  </div>
                  <div className="story-lesson">
                    <FaLightbulb className="lesson-icon" />
                    <span>{story.lesson}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Compétences nécessaires */}
          <div className="skills-section">
            <h2>Compétences de l'entrepreneur</h2>
            <div className="skills-grid">
              {entrepreneurshipSkills.map((skill, index) => (
                <div className="skill-item" key={index}>
                  <FaCheckCircle className="skill-check" />
                  <span>{skill}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Ressources */}
          <div className="resources-section">
            <h2>Ressources pour apprendre</h2>
            <div className="resources-grid">
              {resources.map((resource, index) => (
                <div className="resource-card" key={index}>
                  <h3>{resource.type}</h3>
                  <ul>
                    {resource.items.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* Contact Section */}
          <div className="service-contact">
            <h2>Prêt à lancer votre projet ?</h2>
            <p>Nous vous accompagnons dans la concrétisation de votre idée</p>
            <div className="contact-buttons">
              <Link to="/contact" className="contact-btn primary">
                <FaRocket /> Démarrer mon projet
              </Link>
              <Link to="/rendez-vous" className="contact-btn secondary">
                <FaHandshake /> Conseils personnalisés
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Entrepreneuriat;